// Shared reading of an announcement row, used by BOTH surfaces that render one — the floating
// card and the "What's new" feed — so a card and its feed row can never disagree about which icon
// a line carries or how its date reads.
//
// docs/plans/2026-08-14-product-announcements.md §5: `icon` is a lucide icon NAME chosen by whoever
// authored the payload, and the SHELL resolves it. An unknown name is not an error — announcements
// are data written after this code shipped — so it falls back to a sparkle rather than rendering a
// hole where an icon should be.
import {
  BarChart3,
  Bell,
  CalendarDays,
  Clock,
  Eye,
  FileEdit,
  Gauge,
  History,
  Layers,
  LineChart,
  Lock,
  MoonStar,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Wallet,
  Zap,
} from 'lucide-react'
import type React from 'react'

// Named explicitly, never resolved dynamically off the lucide barrel: a dynamic lookup pulls the
// WHOLE icon set into the operator bundle. Add a name here when a payload needs one.
const ANNOUNCEMENT_ICONS: Record<string, React.ElementType> = {
  'bar-chart-3': BarChart3,
  bell: Bell,
  'calendar-days': CalendarDays,
  clock: Clock,
  eye: Eye,
  'file-edit': FileEdit,
  gauge: Gauge,
  history: History,
  layers: Layers,
  'line-chart': LineChart,
  lock: Lock,
  'moon-star': MoonStar,
  'rotate-ccw': RotateCcw,
  'shield-check': ShieldCheck,
  sparkles: Sparkles,
  target: Target,
  'trending-up': TrendingUp,
  wallet: Wallet,
  zap: Zap,
}

export function announcementIcon(name: string | null | undefined): React.ElementType {
  if (!name) return Sparkles
  return ANNOUNCEMENT_ICONS[name] ?? Sparkles
}

/**
 * "14 Aug 2026" — the way a hotelier writes a date, not an ISO stamp. Formatting only: the value
 * is the backend's `publishedAt`, unmodified.
 */
export function formatAnnouncementDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
